import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';

import { useConferirNaSefaz, useTriagemDePendencias } from '../../shared/api/queries.ts';
import type { GrupoDePendencia, ResultadoDaConferencia } from '../../shared/api/tipos.ts';
import { formatarData, formatarInteiro } from '../../shared/format.ts';
import { DialogoDeDesfecho } from './DialogoDeDesfecho.tsx';

/**
 * A triagem: as pendências da loja reunidas pelo código que a SEFAZ devolveu.
 *
 * É a tela antes da lista. Rejeição em NFC-e quase nunca é um problema por
 * nota — é um erro de configuração do PDV que se repete em todas as vendas
 * daquela tarde —, e trezentas linhas iguais escondem que são três problemas.
 * Agrupadas, são três linhas e três decisões.
 *
 * Os dois botões respondem perguntas diferentes: **conferir** vai perguntar
 * ao estado o que aconteceu de verdade, e é o que resolve a nota que foi
 * autorizada depois e nunca mais passou pelo caixa; **registrar desfecho** é
 * o que o escritório decidiu sobre o que a SEFAZ já respondeu.
 */
export function TriagemPorCodigo({
  empresaId,
  razaoSocial,
  periodo,
  daEquipe,
}: {
  empresaId: string;
  razaoSocial: string;
  /** O período já aplicado na tela, que é o recorte do que vai ser tocado. */
  periodo: { de: string; ate: string };
  /** O contador vê a triagem e não age sobre ela. */
  daEquipe: boolean;
}) {
  const [mostrarResolvidas, setMostrarResolvidas] = useState(false);
  const [grupoEscolhido, setGrupoEscolhido] = useState<GrupoDePendencia | null>(null);
  const [conferencia, setConferencia] = useState<ResultadoDaConferencia | null>(null);

  const filtro = {
    empresaId,
    de: periodo.de === '' ? undefined : periodo.de,
    ate: periodo.ate === '' ? undefined : periodo.ate,
    resolvida: mostrarResolvidas ? undefined : false,
  };
  const { data, isPending, isError, error } = useTriagemDePendencias(filtro);
  const conferir = useConferirNaSefaz();

  const grupos = data?.grupos ?? [];

  async function conferirNaSefaz() {
    const resultado = await conferir.mutateAsync({
      empresaId,
      de: filtro.de,
      ate: filtro.ate,
    });
    setConferencia(resultado);
  }

  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between', mb: 2 }}
        >
          <div>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Triagem por código da SEFAZ
            </Typography>
            <Typography variant="body2" color="text.secondary">
              O mesmo erro costuma repetir em centenas de cupons da mesma tarde — aqui ele é uma
              linha só.
            </Typography>
          </div>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={mostrarResolvidas}
                  onChange={(evento) => setMostrarResolvidas(evento.target.checked)}
                />
              }
              label={
                <Typography variant="body2">Mostrar as que já têm desfecho</Typography>
              }
            />

            {daEquipe && (
              <Tooltip title="Pergunta à SEFAZ, com o certificado do cliente, o que houve com cada nota pendente.">
                <span>
                  <Button
                    variant="contained"
                    startIcon={<CloudSyncOutlinedIcon />}
                    onClick={() => void conferirNaSefaz()}
                    loading={conferir.isPending}
                  >
                    Conferir na SEFAZ
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {conferir.isError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => conferir.reset()}>
            {conferir.error instanceof Error
              ? conferir.error.message
              : 'Não foi possível consultar a SEFAZ.'}
          </Alert>
        )}

        {conferencia !== null && (
          <Alert
            severity={conferencia.interrompidaPor === null ? 'success' : 'warning'}
            sx={{ mb: 2 }}
            onClose={() => setConferencia(null)}
          >
            {formatarInteiro(conferencia.consultadas)} nota(s) consultada(s):{' '}
            {formatarInteiro(conferencia.autorizadas)} autorizada(s),{' '}
            {formatarInteiro(conferencia.canceladas)} cancelada(s) e{' '}
            {formatarInteiro(conferencia.rejeitadas)} sem documento na SEFAZ.
            {conferencia.restantes > 0 &&
              ` Ficaram ${formatarInteiro(conferencia.restantes)} para a próxima rodada.`}
            {conferencia.interrompidaPor !== null && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                A rodada parou antes do fim: {conferencia.interrompidaPor}. A cota de consulta é
                do CNPJ da loja — é a mesma de que o caixa precisa para emitir —, então vale
                tentar de novo mais tarde.
              </Typography>
            )}
          </Alert>
        )}

        {isError ? (
          <Alert severity="error">
            Não foi possível agrupar as pendências.{' '}
            {error instanceof Error ? error.message : ''}
          </Alert>
        ) : isPending ? (
          <Stack spacing={1}>
            {Array.from({ length: 3 }, (_, indice) => (
              <Skeleton key={indice} variant="rounded" height={36} />
            ))}
          </Stack>
        ) : grupos.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            {mostrarResolvidas
              ? 'Nenhuma pendência neste recorte.'
              : 'Nenhuma pendência esperando decisão neste recorte.'}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>O que a SEFAZ respondeu</TableCell>
                  <TableCell align="right">Notas</TableCell>
                  <TableCell align="right">Pendentes</TableCell>
                  <TableCell align="right">Rejeitadas</TableCell>
                  <TableCell>Mais antiga</TableCell>
                  <TableCell align="right">Desfecho</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grupos.map((grupo) => (
                  <TableRow key={grupo.codigo ?? 'sem-codigo'} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={grupo.codigo === null ? 'default' : 'error'}
                        label={grupo.codigo ?? 'sem código'}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 440 }}>
                        {grupo.mensagem ?? 'A nota foi emitida em contingência e ainda não teve resposta.'}
                      </Typography>
                      {grupo.resolvidas > 0 && (
                        <Typography variant="caption" color="success.main">
                          {formatarInteiro(grupo.resolvidas)} já com desfecho
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatarInteiro(grupo.quantidade)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {grupo.pendentes === 0 ? '—' : formatarInteiro(grupo.pendentes)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {grupo.rejeitadas === 0 ? '—' : formatarInteiro(grupo.rejeitadas)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatarData(grupo.maisAntigaEm)}
                    </TableCell>

                    <TableCell align="right">
                      {daEquipe && (
                        <Button
                          size="small"
                          startIcon={<FactCheckOutlinedIcon />}
                          onClick={() => setGrupoEscolhido(grupo)}
                        >
                          Resolver
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <DialogoDeDesfecho
        aberto={grupoEscolhido !== null}
        grupo={grupoEscolhido}
        empresaId={empresaId}
        razaoSocial={razaoSocial}
        periodo={periodo}
        aoFechar={() => setGrupoEscolhido(null)}
      />
    </Card>
  );
}
